import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging';
import {
  registerFcmToken,
  unregisterFcmToken,
  fetchFcmStatus,
  sendFcmTestNotification,
  type FcmStatus,
} from '@vaiu/utils';
import { firebaseConfig as defaultFirebaseConfig, FCM_VAPID_KEY } from '@/config/firebaseConfig';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

interface FCMStatusCache {
  data: FcmStatus;
  timestamp: number;
  expiresAt: number;
}

let fcmStatusCache: FCMStatusCache | null = null;
const FCM_STATUS_CACHE_DURATION = 3 * 60 * 1000;

const isJsonResponse = (response: Response): boolean => {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json');
};

export const initializeFirebase = async (): Promise<void> => {
  try {
    const config = await getFirebaseConfig();

    if (getApps().length === 0) {
      app = initializeApp(config);
      console.log('[FCM] Firebase app initialized');
    } else {
      app = getApps()[0];
      console.log('[FCM] Firebase app already initialized');
    }

    if ('serviceWorker' in navigator && 'PushManager' in window) {
      messaging = getMessaging(app);
      console.log('[FCM] Firebase messaging initialized');
    } else {
      console.warn('[FCM] Not supported in this browser');
    }
  } catch (error) {
    console.error('[FCM] Failed to initialize Firebase:', error);
    throw error;
  }
};

export const getFirebaseConfig = async (): Promise<typeof defaultFirebaseConfig> => {
  try {
    const response = await fetch('/api/v1/firebase/config', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok && isJsonResponse(response)) {
      const data = await response.json();
      if (data?.config?.projectId) {
        return data.config;
      }
    }
  } catch (error) {
    console.warn('[FCM] Could not fetch Firebase config from backend, using bundled config:', error);
  }

  return defaultFirebaseConfig;
};

const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const existing = await navigator.serviceWorker.getRegistration('/sw.js');
    if (existing) {
      return existing;
    }

    return await navigator.serviceWorker.register('/sw.js');
  } catch (error) {
    console.error('[FCM] Failed to resolve service worker registration:', error);
    return null;
  }
};

export const getFCMToken = async (): Promise<string | null> => {
  try {
    if (!messaging) {
      await initializeFirebase();
    }

    if (!messaging) {
      console.error('[FCM] Firebase messaging not initialized');
      return null;
    }

    const serviceWorkerRegistration = await getServiceWorkerRegistration();
    if (!serviceWorkerRegistration) {
      console.error('[FCM] Service worker registration unavailable');
      return null;
    }

    await navigator.serviceWorker.ready;

    const currentToken = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration,
    });

    if (currentToken) {
      console.log('[FCM] Registration token obtained');
      return currentToken;
    }

    console.log('[FCM] No registration token available');
    return null;
  } catch (error) {
    console.error('[FCM] Error getting registration token:', error);
    return null;
  }
};

export const deleteFCMToken = async (): Promise<boolean> => {
  try {
    if (!messaging) {
      return true;
    }

    await deleteToken(messaging);
    console.log('[FCM] Token deleted successfully');
    return true;
  } catch (error) {
    console.error('[FCM] Error deleting token:', error);
    return false;
  }
};

export const setupForegroundMessageListener = (
  onMessageReceived: (payload: any) => void
): (() => void) | null => {
  try {
    if (!messaging) {
      console.error('[FCM] Firebase messaging not initialized');
      return null;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('[FCM] Message received in foreground:', payload);
      onMessageReceived(payload);
    });

    return unsubscribe;
  } catch (error) {
    console.error('[FCM] Error setting up foreground message listener:', error);
    return null;
  }
};

export const subscribeFCM = async (token: string): Promise<boolean> => {
  const result = await registerFcmToken(token);

  if (result.ok) {
    console.log('[FCM] Successfully subscribed via backend');
    return true;
  }

  if (result.reason === 'not_configured') {
    console.warn('[FCM] FCM is not configured on the platform:', result.message);
    return false;
  }

  console.error('[FCM] Error subscribing via backend:', result.message || result.reason);
  return false;
};

export const unsubscribeFCM = async (): Promise<boolean> => {
  const success = await unregisterFcmToken();

  if (success) {
    console.log('[FCM] Successfully unsubscribed via backend');
    return true;
  }

  console.error('[FCM] Error unsubscribing via backend');
  return false;
};

export const getFCMStatus = async (): Promise<FcmStatus> => {
  const now = Date.now();
  if (fcmStatusCache && now < fcmStatusCache.expiresAt) {
    if (import.meta.env.DEV) {
      console.log('[FCM] Status retrieved from cache');
    }
    return fcmStatusCache.data;
  }

  const status = await fetchFcmStatus();

  fcmStatusCache = {
    data: status,
    timestamp: now,
    expiresAt: now + FCM_STATUS_CACHE_DURATION,
  };

  if (import.meta.env.DEV) {
    console.log('[FCM] Status fetched from backend and cached');
  }

  return status;
};

export const sendTestFCMNotification = async (): Promise<boolean> => {
  const success = await sendFcmTestNotification();

  if (success) {
    console.log('[FCM] Test notification sent');
  } else {
    console.error('[FCM] Error sending test notification');
  }

  return success;
};

export const clearFCMStatusCache = (): void => {
  fcmStatusCache = null;
  console.log('[FCM] Status cache cleared');
};

export { messaging, FCM_VAPID_KEY };
